// MIRRORS: panel/util/number.ts — identical copy; edit both, or delete this line on both to declare a deliberate split.
/**
 * Turkish number formatting: thousands separated with a
 * dot, decimals with a comma, and decimals always shown to two places.
 *
 *   100000  → "100.000"
 *   500.5   → "500,50"
 *   7.8     → "7,80"
 *
 * Whole numbers show no decimal part, per her examples. Note this makes money
 * inconsistent — a 7 TL price renders "7" while 7.80 renders "7,80". If prices
 * should always carry two decimals, change `formatPrice` below; it is
 * deliberately a separate export so that decision is one line, not a sweep.
 *
 * NOT for years, ids or counts — `2026` must never render as "2.026".
 */

const trFormatter = (fractionDigits: number) =>
  new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Grouped thousands; two decimals only when the value actually has a fraction. */
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '';
  return trFormatter(Number.isInteger(n) ? 0 : 2).format(n);
}

/**
 * Prices — ALWAYS two decimals, unlike formatNumber.
 *
 *   33    → "33,00"      (not "33")
 *   7.8   → "7,80"
 *   1000  → "1.000,00"
 *
 * Money is the one case where trailing zeros carry meaning: a price list where
 * some rows read "33" and others "33,50" looks broken, and the column no longer
 * aligns on the separator. The database column is numeric(12,2), so two decimals
 * is also exactly what is stored.
 */
export function formatPrice(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '';
  return trFormatter(2).format(n);
}

/**
 * Parses what a Turkish user actually types back into a JS number.
 * Accepts "500,50" and "500.50"; tolerates grouped input like "1.000,50".
 * Returns null when the text is not a usable number, so callers can show a
 * field error instead of silently sending NaN.
 */
export function parseDecimal(input: string): number | null {
  const raw = input.trim();
  if (!raw) return null;

  let normalised = raw;
  if (raw.includes(',')) {
    // Comma present → it is the decimal separator, so any dots are grouping.
    normalised = raw.replace(/\./g, '').replace(',', '.');
  }

  const n = Number(normalised);
  return Number.isFinite(n) ? n : null;
}

/**
 * What a decimal field HOLDS while being typed: digits, at most one comma, at
 * most two decimals. Never grouped — grouping is display only, so the stored
 * value never has to be un-grouped before it is parsed.
 */
export function toDecimalRaw(displayed: string): string {
  const kept = displayed.replace(/[^\d.,]/g, '');

  /**
   * Dots are this field's grouping, with one exception: a dot the user has
   * just typed at the END, when there is no comma yet. Phone keypads offer a
   * dot rather than a comma, and that is the moment to accept it as one.
   *
   * Inferring a decimal point from a dot's POSITION was tried and is wrong in
   * both directions — appending a digit to "1.000" gives "1.0000", and
   * backspacing it gives "1.00"; neither is a decimal, and both look like one.
   */
  const trailingDotIsDecimal = kept.endsWith('.') && !kept.includes(',');
  const raw = trailingDotIsDecimal
    ? `${kept.slice(0, -1).replace(/\./g, '')},`
    : kept.replace(/\./g, '');

  const first = raw.indexOf(',');
  if (first === -1) return raw;
  // One separator, two decimals. Sliced rather than rejected so a paste of
  // "12,3456" becomes "12,34" instead of refusing the whole value.
  return (
    raw.slice(0, first + 1) +
    raw
      .slice(first + 1)
      .replace(/,/g, '')
      .slice(0, 2)
  );
}

/** Dots every three digits, right to left. Integer text in, grouped text out. */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * How a decimal field LOOKS while being typed: `100000` → `100.000`,
 * `1234,5` → `1.234,5`.
 *
 * The fraction is left exactly as typed — padding "7," to "7,00" mid-entry
 * would move the caret and fight the user. Money gets its trailing zeros from
 * formatPrice once the value is displayed rather than edited.
 */
export function formatDecimalInput(raw: string): string {
  if (!raw) return '';
  const [whole, fraction] = raw.split(',');
  const grouped = groupThousands(whole);
  return fraction === undefined ? grouped : `${grouped},${fraction}`;
}

/**
 * Keystroke filter for decimal fields: digits plus a single separator. Lets the
 * user type "500," mid-entry without the value being rewritten under them.
 */
export function sanitiseDecimalInput(input: string): string {
  const cleaned = input.replace(/[^0-9.,]/g, '');
  const firstSeparator = cleaned.search(/[.,]/);
  if (firstSeparator === -1) return cleaned;
  const head = cleaned.slice(0, firstSeparator + 1);
  const tail = cleaned.slice(firstSeparator + 1).replace(/[.,]/g, '');
  return head + tail.slice(0, 2);
}
